import { cn } from "@/lib/utils";

// Sprint 8 — Sparkline SVG inline pour usage liste (companies, contacts,
// dev_tasks). 96×26 par défaut. Si data null/empty → rendu "—" muted.
// Couleur auto : vert si trending up (last > first), gris sinon.
// Source data : tableau de N nombres (typiquement 30 derniers jours
// d'activity depuis PostHog ou agrégat local).

interface SparklineProps {
  data: number[] | null | undefined;
  width?: number;
  height?: number;
  accent?: string;
  className?: string;
}

export const Sparkline = ({
  data,
  width = 96,
  height = 26,
  accent,
  className,
}: SparklineProps) => {
  if (!data || data.length === 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center text-muted-foreground/60 text-xs tabular-nums",
          className,
        )}
        style={{ width, height }}
        aria-label="Pas de données"
      >
        —
      </span>
    );
  }

  if (data.length === 1) {
    // Un seul point : afficher un dot centré
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className={className}
        aria-label="1 point de donnée"
      >
        <circle
          cx={width / 2}
          cy={height / 2}
          r="2"
          fill={accent ?? "currentColor"}
        />
      </svg>
    );
  }

  const pad = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = (width - pad * 2) / (data.length - 1);

  const points = data
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (height - pad * 2) * (1 - (v - min) / range);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const last = data[data.length - 1];
  const first = data[0];
  const trendUp = last > first;
  const stroke = accent ?? (trendUp ? "rgb(16 185 129)" : "rgb(113 113 122)"); // emerald-500 | zinc-500

  const lastX = pad + (data.length - 1) * stepX;
  const lastY = pad + (height - pad * 2) * (1 - (last - min) / range);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      className={className}
      aria-label={`Activité ${data.length} points ${trendUp ? "tendance haussière" : "tendance stable"}`}
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2" fill={stroke} />
    </svg>
  );
};
